/* eslint-disable indent */
import {
  Backdrop,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableContainer,
  TableFooter,
  useMediaQuery,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGeneralChargesContext } from 'contexts/GeneralChargesContext';
import { useTableGeneralCharges } from './hooks';
import SkeletonCharges from 'views/orders/new-charges/components/skeleton';
import { ChargesTableHead } from './components/ChargesTableHead';
import { HUD_1 } from 'utils/constants';
import { ChargesTableRow } from './components/ChargesTableRow';
import { DeleteConfirmationModal } from 'components/delete-confirmation';
import DocumentModalCalculation from 'views/orders/new-charges/charges-e/components/document-fees-modal/document-modal-calculation';
import { extractData } from 'views/orders/new-charges/charges-e/components/document-fees-modal/services';
import { SplitPayeePayer } from 'views/orders/new-charges/components/split-payee/split-payee';
import { Footer } from '../Footer';
import { TotalTableFooter } from './components/TotalTableFooter';
import { ContainedButton } from 'components/ContainedButton';
import { OutlinedButton } from 'components/OutlinedButton';
import { FormProvider } from 'react-hook-form';

const useStyles = makeStyles((theme: any) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1000,
    color: theme.palette.common.white,
  },
  container: {
    padding: `${theme.spacing(1.5)}`,
    [theme.breakpoints.down('sm')]: {
      padding: `${theme.spacing(0.5)}`,
    },
  },
  mobileActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1),
  },
  scrollWrapper: {
    overflowX: 'auto',
    width: '100%',
    '&&::-webkit-scrollbar': { height: theme.spacing(1) },
    scrollbarColor: `${theme.palette.secondary.main} ${theme.palette.background.paper}`,
    scrollbarWidth: 'thin',
    '&&::-webkit-scrollbar-track': { borderRadius: theme.spacing(0.5) },
    '&&::-webkit-scrollbar-thumb': {
      background: theme.palette.secondary.main,
      borderRadius: 4,
    },
    '&&::-webkit-scrollbar-thumb:hover': { background: theme.palette.secondary.dark },
  },
  buttonContainer: {
    padding: `${theme.spacing(2)}`,
  },
  table: {
    padding: `${theme.spacing(2)}`,
    minWidth: 900,
  },
  searchContact: {
    '& .MuiInputLabel-filled': {
      color: theme.palette.primary.dark,
    },
    '& .MuiFilledInput-root': {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.primary.dark,
      border: 'unset',
      borderRadius: '4px',

      '&.MuiFilledInput-underline::before': {
        content: 'unset',
      },

      '&.MuiFilledInput-underline::after': {
        content: 'unset',
      },
    },
  },
  numberField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.primary.dark,
      borderRadius: '5px',
      fontSize: '13px',
      display: 'flex',
      justifyContent: 'flex-end',

      '& .MuiOutlinedInput-notchedOutline': {
        border: 'unset',
      },
      '& .MuiOutlinedInput-input ': {
        textAlign: 'right',
        width: '4rem',
        padding: '8px 6px',
      },
    },
  },
  inputChangeOverride: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.warning.main,
    },
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.default,
      borderRadius: '5px',
      color: theme.palette.primary.dark,
      fontSize: '0.8rem',

      '& .MuiOutlinedInput-input': {
        padding: '10px !important',
      },

      '& .MuiOutlinedInput-notchedOutline': {
        border: 'unset',
      },
    },
  },
  numberInput: {
    '& .MuiOutlinedInput-root': {
      width: '35px',
      height: '35px',
      backgroundColor: theme.palette.secondary.main,
      borderRadius: '5px',
      color: theme.palette.primary.dark,

      '& .MuiOutlinedInput-notchedOutline': {
        border: 'unset',
      },
      '& .MuiOutlinedInput-input ': {
        padding: `${theme.spacing(0.25)}`,
        textAlign: 'center',
      },
    },
  },
  numberContainer: {
    width: '30px',
    height: '30px',
    backgroundColor: theme.palette.secondary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '5px',
    color: theme.palette.primary.dark,
  },
  tableChargeRow: {
    padding: `8px ${theme.spacing(0.5)}`,
    fontSize: '0.8rem',
  },
  addEntry: {
    textTransform: 'capitalize',
  },
  updateCharges: {
    textTransform: 'capitalize',
  },
  tableMenuRow: {
    padding: `${theme.spacing(0.25)}`,
    textAlign: 'center',
    cursor: 'pointer',
  },
  changeAllLabel: {
    fontSize: theme.typography.caption.fontSize,
    color: theme.palette.tab.offselected,
    whiteSpace: 'nowrap',
  },
}));

const lettersPerMonth = ['G'];

type Props = {
  isLoadingInfo?: boolean;
  orderType: string;
};

const TableCharges: FC<Props> = ({ isLoadingInfo, orderType }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    accounting,
    chargesLetter: currentLetter,
    accountingFilter,
    hideTax,
    hideAddEntry,
    policyIsLoading,
    textPredectiveCharges,
    handleCloseModal,
    updateModalState,
  } = useGeneralChargesContext();

  const accountingData = useMemo(
    () => accounting,
    [accounting]
  );

  const {
    hanldeUpdateCharge,
    isLoading,
    handleDeleteCharge,
    formatedAccountingData,
    hanldeAddEmptyCharges,
    control,
    handleSubmit,
    isValid,
    handleOpenDeleteDocumentChargeModal,
    modalState,
    handleOpenEditDocumentChargeModal,
    handleOpenSplitAmountsModal,
    handleOpenChangeDisclosurePolicy,
    chargesForm,
  } = useTableGeneralCharges({
    accountingData,
    currentLetter,
    accountingFilter
  });

  const onSubmit = () =>
    handleSubmit((data) => {
      const arrayChargesToUpload = data.charges
        .filter(({ description, disabled }) => (description !== '' && description !== null) && disabled === false)
        .map((charge) => {
          return {
            ...charge,
          };
        })
        .map((charge) => {
          const newCharge = charge as any;
          if (charge.code === "document") {
            delete newCharge.number;
          }
          delete newCharge.kind;
          delete newCharge.payee;
          delete newCharge.amount_override;
          delete newCharge.amount_calculated;
          delete newCharge.payees;
          delete newCharge.payers;
          delete newCharge.taxes;
          delete newCharge.disabled;
          delete newCharge.code;
          delete newCharge.payee_id;
          return newCharge;
        });

      hanldeUpdateCharge(arrayChargesToUpload);
    })();

  const isOrderTypeNotHUD1 = orderType !== HUD_1;
  const isMobile = !useMediaQuery((theme: any) => theme.breakpoints.up('lg'));

  return (
    <FormProvider {...chargesForm}>
      <Box className={classes.container}>
        {isMobile && (
          <Box>
            <Box className={classes.mobileActions}>
              {currentLetter !== 'J' && !hideAddEntry && (
                <OutlinedButton
                  text={t('charges:add-entry')}
                  size="small"
                  buttonColor="primary"
                  onClick={hanldeAddEmptyCharges}
                />
              )}
              {currentLetter && ['E', '1200'].includes(currentLetter) && (
                <OutlinedButton
                  text={t('accounting:add-document-entry')}
                  size="small"
                  buttonColor="primary"
                  onClick={() => {
                    updateModalState({ modal: 'addDocument', charge: undefined, rowIndex: undefined });
                  }}
                />
              )}
              <ContainedButton
                text={t('common:save')}
                size="small"
                disabled={!isValid}
                onClick={onSubmit}
              />
            </Box>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span className={classes.changeAllLabel}>{t('charges:change-all')}</span>
              <OutlinedButton
                text={t('common:payeesLabel')}
                size="small"
                onClick={() => handleOpenSplitAmountsModal({} as any, 'Payee', true)}
              />
              <OutlinedButton
                text={t('common:payersLabel')}
                size="small"
                onClick={() => handleOpenSplitAmountsModal({} as any, 'Payer', true)}
              />
              {!hideTax && (
                <OutlinedButton
                  text={t('charges:taxes')}
                  size="small"
                  onClick={() => handleOpenSplitAmountsModal({} as any, 'Tax', true)}
                />
              )}
            </Box>
            {formatedAccountingData.map((charge, index) => (
              <ChargesTableRow
                key={`${charge.accounting_id}|${charge.number}`}
                charge={charge}
                index={index}
                currentLetter={currentLetter}
                formatedAccountingData={formatedAccountingData}
                lettersPerMonth={lettersPerMonth}
                textPredectiveCharges={textPredectiveCharges}
                hideTax={hideTax}
                isOrderTypeNotHUD1={isOrderTypeNotHUD1}
                isMobile={isMobile}
                onDeleteCharge={handleOpenDeleteDocumentChargeModal}
                onEditCharge={handleOpenEditDocumentChargeModal}
                onEditSplitAmount={handleOpenSplitAmountsModal}
              />
            ))}
          </Box>
        )}

        {!isMobile && (
          <Box className={classes.scrollWrapper}>
            <TableContainer>
              <form>
                <Table className={classes.table}>
                  <ChargesTableHead
                    hideTax={hideTax}
                    currentLetter={currentLetter}
                    lettersPerMonth={lettersPerMonth}
                    isOrderTypeNotHUD1={isOrderTypeNotHUD1}
                    onEditSplitPayers={handleOpenSplitAmountsModal}
                  />
                  <TableBody style={{ maxHeight: '460px', overflowY: 'scroll', overflow: 'auto' }}>
                    {formatedAccountingData.map((charge, index) => (
                      <ChargesTableRow
                        key={`${charge.accounting_id}|${charge.number}`}
                        charge={charge}
                        index={index}
                        currentLetter={currentLetter}
                        formatedAccountingData={formatedAccountingData}
                        lettersPerMonth={lettersPerMonth}
                        textPredectiveCharges={textPredectiveCharges}
                        hideTax={hideTax}
                        isOrderTypeNotHUD1={isOrderTypeNotHUD1}
                        isMobile={isMobile}
                        onDeleteCharge={handleOpenDeleteDocumentChargeModal}
                        onEditCharge={handleOpenEditDocumentChargeModal}
                        onEditSplitAmount={handleOpenSplitAmountsModal}
                      />
                    ))}
                  </TableBody>
                  {isOrderTypeNotHUD1 && (
                    <TableFooter>
                      <TotalTableFooter
                        control={control}
                        currentLetter={currentLetter}
                        hideTax={hideTax}
                        isOrderTypeNotHUD1={isOrderTypeNotHUD1}
                      />
                    </TableFooter>
                  )}
                </Table>
              </form>
            </TableContainer>
          </Box>
        )}

        {!isMobile && (
          <Footer
            isFormValid={isValid}
            hanldeAddEmptyCharges={hanldeAddEmptyCharges}
            handleOpenChangeDisclosurePolicy={handleOpenChangeDisclosurePolicy}
            onSubmit={onSubmit}
          />
        )}

        <Backdrop
          className={classes.backdrop}
          open={isLoading || Boolean(policyIsLoading) || Boolean(isLoadingInfo)}
        >
          <CircularProgress color="secondary" />
        </Backdrop>

        <DeleteConfirmationModal
          isOpen={modalState.modal === 'deleteDocument'}
          title={t('orders:deleteChargeTitle')}
          confirmationMessage={t('orders:deleteChargeConfirmation', {
            description: modalState.modal === 'deleteDocument'
              ? extractData(modalState.charge!.description)?.document
              : ''
          })}
          onClose={handleCloseModal}
          onConfirm={handleDeleteCharge}
          isRemoveLoading={isLoading}
        />

        {currentLetter && ['1200', 'E'].includes(currentLetter) &&
          (modalState.modal === 'editDocument' || modalState.modal === 'addDocument') && (
            <DocumentModalCalculation
              open
              documentAccountingId={
                modalState.modal === 'editDocument' ? modalState.charge.accounting_id! : undefined
              }
              documentDescription={
                modalState.modal === 'editDocument' ? modalState.charge.description : undefined
              }
              onClose={() => {
                handleCloseModal();
              }}
              letter={currentLetter === '1200' ? 'L' : currentLetter}
            />
          )}

        {modalState.modal === 'editSplit' && (
          <SplitPayeePayer
            open
            kind={modalState.kind}
            letter={currentLetter}
            charges={modalState.charges}
            onClose={handleCloseModal}
            onSuccess={() => handleCloseModal()}
          />
        )}

        {modalState.modal === 'confirmationModal' && (
          <DeleteConfirmationModal
            isOpen
            title={modalState.confirmationTitle}
            confirmationMessage={modalState.confirmationMessage}
            onConfirm={modalState.onConfirm}
            isRemoveLoading={modalState.isLoading}
            onClose={handleCloseModal}
            buttonConfirmText={t('orders:confirmButton')}
          />
        )}
      </Box>
    </FormProvider>
  );
};

interface GeneralTabChargesProps {
  orderType: string;
}

export const GeneralTableCharges: FC<GeneralTabChargesProps> = ({ orderType }) => {
  const { isFetching, accounting } = useGeneralChargesContext();

  if (isFetching && accounting.length === 0) return <SkeletonCharges />;

  return <TableCharges orderType={orderType} />;
};
